import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePackageAccess } from "@/lib/packageAccess";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Json } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { Palette, Upload, RefreshCw, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import MediaPickerDialog from "@/components/MediaPickerDialog";
import { templateModels } from "@/lib/templates";
import { buildDeployPayload } from "@/lib/deployHelper";
import { assertPleskProvisionSucceeded } from "@/lib/pleskProvision";

const colorPresets = [
  { name: "Ocean", primary: "#0ea5e9", accent: "#38bdf8", bg: "#f0f9ff" },
  { name: "Forest", primary: "#16a34a", accent: "#4ade80", bg: "#f0fdf4" },
  { name: "Sunset", primary: "#ea580c", accent: "#fb923c", bg: "#fff7ed" },
  { name: "Royal", primary: "#7c3aed", accent: "#a78bfa", bg: "#faf5ff" },
  { name: "Crimson", primary: "#dc2626", accent: "#f87171", bg: "#fef2f2" },
  { name: "Slate", primary: "#475569", accent: "#94a3b8", bg: "#f8fafc" },
];

const fontPairs = [
  { display: "Playfair Display", body: "Source Sans 3", label: "Classic Editorial" },
  { display: "Inter", body: "Inter", label: "Modern Clean" },
  { display: "Merriweather", body: "Open Sans", label: "Readable" },
  { display: "DM Serif Display", body: "DM Sans", label: "Contemporary" },
];

type Props = { siteId: string; domain?: string | null; imported?: boolean; originalTheme?: string | null };

export default function DesignTab({ siteId, domain, imported = false, originalTheme = null }: Props) {
  const queryClient = useQueryClient();
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const { data: site, isLoading } = useQuery({
    queryKey: ["website-detail", siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("websites")
        .select("id, name, logo_url, theme_config, template_id")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const syncToServer = async () => {
    if (!domain) return;
    setSyncing(true);
    try {
      const payload = await buildDeployPayload(siteId, domain);
      const { data, error } = await supabase.functions.invoke("plesk-provision", {
        body: { ...payload, sync_only: true },
      });
      if (error) throw error;
      assertPleskProvisionSucceeded(data);
      toast.success("Design synced to site");
    } catch (err: any) {
      toast.error(err.message || "Design sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (updates: { logo_url?: string; theme_config?: Json; template_id?: string | null }) => {
      const { error } = await supabase.from("websites").update(updates).eq("id", siteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["website-detail", siteId] });
      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Design updated");
      // Auto-sync to live server if domain exists
      if (domain) syncToServer();
    },
  });

  const themeConfig = (site?.theme_config as any) || {};
  const packageAccess = usePackageAccess();
  const planSlug = String(packageAccess.package?.slug ?? "");
  const whiteLabelAllowed = packageAccess.isAdmin || /agency|enterprise/.test(planSlug);
  const programmaticAllowed = packageAccess.isAdmin || /network|agency|enterprise/.test(planSlug);

  const handleColorSelect = (preset: typeof colorPresets[0]) => {
    updateMutation.mutate({
      theme_config: { ...themeConfig, colorPreset: preset.name, colors: preset },
    });
  };

  const handleFontSelect = (pair: typeof fontPairs[0]) => {
    updateMutation.mutate({
      theme_config: { ...themeConfig, fonts: pair },
    });
  };

  const handleLogoSelect = (url: string) => {
    updateMutation.mutate({ logo_url: url });
    setShowMediaPicker(false);
  };

  const handleTemplateSelect = (templateId: string) => {
    if (imported && templateId && originalTheme) {
      const ok = window.confirm(
        `This site was imported with the theme "${originalTheme}" already installed. ` +
        `Selecting a new template will overwrite it on the next sync. Continue?`
      );
      if (!ok) return;
    }
    updateMutation.mutate({ template_id: templateId || null });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-1">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const ads = (themeConfig.ads as any) || {};
  const setTheme = (partial: Record<string, unknown>) => updateMutation.mutate({ theme_config: { ...themeConfig, ...partial } });

  return (
    <div className="space-y-6 p-1">
      {/* Branding & advertising: white label needs the Agency plan, programmatic (GAM + Prebid) needs Network. */}
      <div className="rounded-lg border border-border p-3 space-y-3">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Marque & publicité</h4>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Marque blanche <span className="text-xs text-muted-foreground">(retire « Propulsé par Delapresse » du site)</span></span>
          <input type="checkbox" className="h-4 w-4" checked={!!themeConfig.white_label} disabled={!whiteLabelAllowed}
            onChange={(e) => setTheme({ white_label: e.target.checked })} />
        </label>
        {!whiteLabelAllowed && <p className="text-[11px] text-amber-700">Disponible à partir du forfait Agency.</p>}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <label className="text-xs space-y-1">
            <span className="text-muted-foreground">Régie</span>
            <select className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm" value={ads.provider || "adsense"}
              onChange={(e) => setTheme({ ads: { ...ads, provider: e.target.value } })}>
              <option value="adsense">Google AdSense</option>
              <option value="gam" disabled={!programmaticAllowed}>Google Ad Manager + Prebid (programmatique)</option>
            </select>
          </label>
          {(ads.provider || "adsense") === "adsense" ? (
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Client AdSense (ca-pub-…)</span>
              <input className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm" value={ads.client || ""}
                onChange={(e) => setTheme({ ads: { ...ads, client: e.target.value.trim() } })} placeholder="ca-pub-1234567890" />
            </label>
          ) : (
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Network code GAM</span>
              <input className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm" value={ads.network_code || ""}
                onChange={(e) => setTheme({ ads: { ...ads, network_code: e.target.value.trim() } })} placeholder="21987654321" />
            </label>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {(["top", "inarticle", "sidebar", "footer"] as const).map((slot) => (
            <label key={slot} className="text-xs space-y-1">
              <span className="text-muted-foreground">Slot {slot}</span>
              <input className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm" value={(ads.slots || {})[slot] || ""}
                onChange={(e) => setTheme({ ads: { ...ads, slots: { ...(ads.slots || {}), [slot]: e.target.value.trim() } } })}
                placeholder={(ads.provider || "adsense") === "adsense" ? "ID d'emplacement" : "/network/ad-unit"} />
            </label>
          ))}
        </div>
        {(ads.provider || "adsense") === "gam" && (
          <label className="text-xs space-y-1 block">
            <span className="text-muted-foreground">Prebid : enchérisseurs (JSON, ex. [{"{"}"bidder":"appnexus","params":{"{"}"placementId":123{"}"}{"}"}])</span>
            <textarea className="min-h-16 w-full rounded-md border border-border bg-background px-2 py-1 font-mono text-xs" value={ads.prebid_bidders_json || ""}
              onChange={(e) => setTheme({ ads: { ...ads, prebid_bidders_json: e.target.value } })} />
          </label>
        )}
        {!programmaticAllowed && <p className="text-[11px] text-muted-foreground">La programmatique (Ad Manager + Prebid) est incluse à partir du forfait Network.</p>}
      </div>

      {/* Logo */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Logo</h4>
        <div className="flex items-center gap-3">
          {site?.logo_url ? (
            <img src={site.logo_url} alt="" className="h-12 w-12 rounded-lg object-cover border border-border" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent border border-border">
              <Palette className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <button
            onClick={() => setShowMediaPicker(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent transition-colors"
          >
            <Upload className="h-3 w-3" /> Change Logo
          </button>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Template</h4>
        <p className="mb-2 text-[11px] text-muted-foreground">Choose the theme package to install on the live site during deploy.</p>
        {imported && originalTheme && (
          <div className="mb-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400">
            ⚠ This site was imported with the theme <span className="font-mono font-semibold">{originalTheme}</span>. Choosing a new template will replace it.
          </div>
        )}
        <select
          value={site?.template_id ?? ""}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Keep current/default theme</option>
          {templateModels.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name} — {template.tagline}
            </option>
          ))}
        </select>
      </div>

      {/* Color Palette */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Color Palette</h4>
        <div className="grid grid-cols-3 gap-2">
          {colorPresets.map((preset) => (
            <button
              key={preset.name}
              onClick={() => handleColorSelect(preset)}
              className={`rounded-lg border p-2 text-left transition-all hover:shadow-sm ${
                themeConfig.colorPreset === preset.name
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-border"
              }`}
            >
              <div className="flex gap-1 mb-1">
                <div className="h-4 w-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                <div className="h-4 w-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                <div className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: preset.bg }} />
              </div>
              <p className="text-[10px] font-medium text-foreground">{preset.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Typography */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Typography</h4>
        <div className="space-y-2">
          {fontPairs.map((pair) => (
            <button
              key={pair.label}
              onClick={() => handleFontSelect(pair)}
              className={`w-full rounded-lg border p-3 text-left transition-all hover:shadow-sm ${
                themeConfig.fonts?.label === pair.label
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-border"
              }`}
            >
              <p className="text-sm font-semibold text-foreground" style={{ fontFamily: pair.display }}>
                {pair.label}
              </p>
              <p className="text-[10px] text-muted-foreground">{pair.display} + {pair.body}</p>
            </button>
          ))}
        </div>
      </div>

      {showMediaPicker && (
        <MediaPickerDialog
          open={showMediaPicker}
          onOpenChange={(v) => !v && setShowMediaPicker(false)}
          onSelect={handleLogoSelect}
        />
      )}
    </div>
  );
}
